<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'next/button/Button.vue';
import { useBugReportRecording } from 'dashboard/composables/useBugReportRecording';
const emit = defineEmits(['update:modelValue', 'recording', 'busy']);
const route = useRoute();
const accountId = computed(() => route.params.accountId);
const {
  uploadError,
  cancelUpload,
  state,
  error,
  preview,
  events,
  attachedToken,
  includeVideo,
  supported,
  start,
  stop,
  attach,
  discard,
} = useBugReportRecording(accountId, value => emit('recording', value));
watch(attachedToken, value => emit('update:modelValue', value));
watch(state, value =>
  emit(
    'busy',
    ['starting', 'recording', 'processing', 'uploading', 'review'].includes(
      value
    )
  )
);
</script>

<template>
  <section class="space-y-3 rounded-lg border border-n-weak p-4">
    <h3 class="font-semibold">{{ $t('BUG_REPORTS.recording.title') }}</h3>
    <p class="text-sm text-n-slate-11">
      {{ $t('BUG_REPORTS.recording.notice') }}
    </p>
    <p v-if="!supported">{{ $t('BUG_REPORTS.recording.unsupported') }}</p>
    <p v-if="error" role="alert">
      {{
        $t(
          uploadError
            ? `BUG_REPORTS.upload.${uploadError}`
            : 'BUG_REPORTS.recording.error'
        )
      }}
    </p>
    <Button
      v-if="state === 'idle'"
      class="!text-n-slate-12 dark:!text-n-slate-1"
      type="button"
      :disabled="!supported"
      :label="$t('BUG_REPORTS.recording.start')"
      @click="start"
    />
    <p
      v-if="['starting', 'processing', 'uploading'].includes(state)"
      role="status"
    >
      {{ $t('BUG_REPORTS.loading') }}
    </p>
    <Button
      v-if="state === 'uploading'"
      type="button"
      variant="ghost"
      :label="$t('BUG_REPORTS.upload.cancel')"
      @click="cancelUpload"
    />
    <template v-if="['review', 'attached'].includes(state)">
      <video v-if="preview" :src="preview" controls class="w-full rounded-lg" />
      <label v-if="state === 'review' && preview" class="flex gap-2">
        <input v-model="includeVideo" type="checkbox" />{{
          $t('BUG_REPORTS.recording.include_video')
        }}
      </label>
      <details>
        <summary>{{ $t('BUG_REPORTS.recording.events') }}</summary>
        <pre class="max-h-48 overflow-auto text-xs">{{
          JSON.stringify(events, null, 2)
        }}</pre>
      </details>
      <p v-if="state === 'attached'">
        {{ $t('BUG_REPORTS.recording.attached') }}
      </p>
      <div class="flex gap-2">
        <Button
          v-if="state === 'review'"
          class="!text-n-slate-12 dark:!text-n-slate-1"
          type="button"
          :label="$t('BUG_REPORTS.recording.attach')"
          @click="attach"
        />
        <Button
          type="button"
          :label="$t('BUG_REPORTS.recording.discard')"
          variant="ghost"
          @click="discard"
        />
      </div>
    </template>
    <Teleport to="body">
      <aside
        v-if="state === 'recording'"
        role="status"
        class="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-n-weak bg-n-background p-4 shadow-xl"
      >
        <span class="text-n-ruby-11">{{
          $t('BUG_REPORTS.recording.active')
        }}</span>
        <Button
          class="!text-n-slate-12 dark:!text-n-slate-1"
          :label="$t('BUG_REPORTS.recording.stop')"
          @click="stop"
        />
      </aside>
    </Teleport>
  </section>
</template>
